/**
 * Suppliers API client (Phase 1 Steps 3–4).
 * Uses relative `/api` — Vite proxy injects X-Api-Token server-side.
 * Do NOT set X-Api-Token from the browser.
 */

import type { Supplier, SupplierInput, SupplierStatus } from './types'

export type SuppliersApiError = {
  ok: false
  error: { code: string; message: string; status: number }
}

export class SuppliersApiClientError extends Error {
  readonly code: string
  readonly status: number

  constructor(code: string, message: string, status: number) {
    super(message)
    this.name = 'SuppliersApiClientError'
    this.code = code
    this.status = status
  }
}

type SupplierApiDto = {
  id: string
  code: string
  name: string
  category_ids: unknown
  address: string | null
  contact: string | null
  phone: string | null
  email: string | null
  tax_code: string | null
  default_vat_rate: number | null
  payment_term_id: string | null
  lead_time_days: number | null
  status: SupplierStatus
  note: string | null
  created_at: string
  updated_at: string
}

async function parseJson<T>(res: Response): Promise<T> {
  return (await res.json()) as T
}

function throwIfApiError(res: Response, body: unknown, fallback: string): void {
  if (res.ok) return
  const err = body as SuppliersApiError
  throw new SuppliersApiClientError(
    err.error?.code ?? 'HTTP_ERROR',
    err.error?.message ?? `${fallback} (${res.status})`,
    err.error?.status ?? res.status,
  )
}

function supplierInputToBody(input: SupplierInput): Record<string, unknown> {
  return {
    code: input.code,
    name: input.name,
    category_ids: input.category_ids,
    created_from: input.created_from,
    address: input.address,
    contact_person: input.contact_person,
    phone: input.phone,
    email: input.email,
    tax_code: input.tax_code,
    default_vat_rate: input.default_vat_rate,
    payment_term_id: input.payment_term_id,
    lead_time_days: input.lead_time_days,
    status: input.status,
    note: input.note,
  }
}

/** Map API DTO → UI Supplier (contact → contact_person). */
export function supplierApiDtoToUi(dto: SupplierApiDto): Supplier {
  const categories = Array.isArray(dto.category_ids)
    ? (dto.category_ids as unknown[]).map(String)
    : []
  return {
    id: dto.id,
    code: dto.code,
    name: dto.name,
    category_ids: categories,
    address: dto.address ?? undefined,
    contact_person: dto.contact ?? undefined,
    phone: dto.phone ?? undefined,
    email: dto.email ?? undefined,
    tax_code: dto.tax_code ?? undefined,
    // UI type requires number; seeded suppliers always have VAT. Null → 8 fallback.
    default_vat_rate: dto.default_vat_rate ?? 8,
    payment_term_id: dto.payment_term_id ?? '',
    lead_time_days: dto.lead_time_days ?? 0,
    status: dto.status,
    note: dto.note ?? undefined,
    created_at: dto.created_at,
    updated_at: dto.updated_at,
  }
}

export async function listSuppliersApi(): Promise<Supplier[]> {
  const res = await fetch('/api/suppliers')
  const body = await parseJson<
    { ok: true; suppliers: SupplierApiDto[] } | SuppliersApiError
  >(res)
  throwIfApiError(res, body, 'Failed to list suppliers')
  return (body as { suppliers: SupplierApiDto[] }).suppliers.map(
    supplierApiDtoToUi,
  )
}

export async function getSupplierApi(id: string): Promise<Supplier | null> {
  const res = await fetch(`/api/suppliers/${encodeURIComponent(id)}`)
  if (res.status === 404) return null
  const body = await parseJson<
    { ok: true; supplier: SupplierApiDto } | SuppliersApiError
  >(res)
  throwIfApiError(res, body, 'Failed to get supplier')
  return supplierApiDtoToUi((body as { supplier: SupplierApiDto }).supplier)
}

export async function createSupplierApi(input: SupplierInput): Promise<Supplier> {
  const res = await fetch('/api/suppliers', {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(supplierInputToBody(input)),
  })
  const body = await parseJson<
    { ok: true; supplier: SupplierApiDto } | SuppliersApiError
  >(res)
  throwIfApiError(res, body, 'Tạo NCC thất bại')
  return supplierApiDtoToUi((body as { supplier: SupplierApiDto }).supplier)
}

export async function updateSupplierApi(
  id: string,
  input: SupplierInput,
): Promise<Supplier> {
  const res = await fetch(`/api/suppliers/${encodeURIComponent(id)}`, {
    method: 'PATCH',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(supplierInputToBody(input)),
  })
  const body = await parseJson<
    { ok: true; supplier: SupplierApiDto } | SuppliersApiError
  >(res)
  throwIfApiError(res, body, 'Cập nhật NCC thất bại')
  return supplierApiDtoToUi((body as { supplier: SupplierApiDto }).supplier)
}

export async function setSupplierStatusApi(
  id: string,
  status: SupplierStatus,
): Promise<Supplier> {
  const res = await fetch(`/api/suppliers/${encodeURIComponent(id)}`, {
    method: 'PATCH',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ status }),
  })
  const body = await parseJson<
    { ok: true; supplier: SupplierApiDto } | SuppliersApiError
  >(res)
  throwIfApiError(res, body, 'Đổi trạng thái NCC thất bại')
  return supplierApiDtoToUi((body as { supplier: SupplierApiDto }).supplier)
}

export async function deleteSupplierApi(id: string): Promise<void> {
  const res = await fetch(`/api/suppliers/${encodeURIComponent(id)}`, {
    method: 'DELETE',
  })
  const body = (await parseJson<
    { ok: true } | SuppliersApiError
  >(res).catch(() => null)) as { ok: true } | SuppliersApiError | null
  throwIfApiError(res, body, 'Xóa NCC thất bại')
}
